import { memo, useCallback } from 'react';

import { type SearchBoardsResponse } from '@air/api/types';
import { ChevronRightIcon } from '@air/design-icons';

import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { BoardSuggestionBreadcrumbs } from '~/components/SearchBar/Suggestion/BoardSuggestionBreadcrumbs';
import { SearchCommandItem } from '~/components/SearchCommand/SearchCommandItem';

export type SearchCommandBoardListItemProps = {
  board: SearchBoardsResponse;
  onSelect: (board: SearchBoardsResponse) => void;
};

export const SearchCommandBoardListItem = memo(({ board, onSelect: _onSelect }: SearchCommandBoardListItemProps) => {
  const onSelect = useCallback(() => _onSelect(board), [board, _onSelect]);

  return (
    <SearchCommandItem data-type="board" onSelect={onSelect} value={board.id}>
      <div className="shrink-0 rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 p-1">
        <BoardThumbnail board={board} height={40} sizes="40px" width={72} />
      </div>

      <div className="min-w-0 grow px-1">
        <div className="truncate font-medium ads:text-primary text-14 text-gray-12">{board.title}</div>
        {/* biome-ignore lint/suspicious/noUnnecessaryConditions: board search can omit ancestors on some hits despite SearchBoardsResponse typing */}
        {board.ancestors?.length ? <BoardSuggestionBreadcrumbs ancestors={board.ancestors} /> : null}
      </div>
      <div className="flex size-8 shrink-0 items-center opacity-0 group-hover/command-item:opacity-100 group-data-[selected=true]/command-item:opacity-100">
        <ChevronRightIcon className="ads:text-icon-emphasis text-gray-12" size="sm" />
      </div>
    </SearchCommandItem>
  );
});

SearchCommandBoardListItem.displayName = 'SearchCommandBoardListItem';
